/**
 * Phone answer screen for a checked-in team (PHONE or MIXED nights).
 *
 * Follows the host: the round and question the host shows are highlighted,
 * and the screen jumps to the host's round. Each round shows whether it is
 * open or closed (the host decides). The round bonus stays on paper: the
 * host enters bonus points in the scoring grid. Every answer is kept on the
 * phone first (utils/answerQueue.ts), then sent in a batch. Failed sends
 * retry with back-off and again when the phone comes back online. Each
 * question shows whether its answer is sent, waiting, or not sent because
 * the round closed.
 *
 * The server never sends answers or other teams' answers to this screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Badge, Box, Button, Callout, Card, Flex, Heading, Text, TextField } from '@radix-ui/themes';
import {
  fetchPlay,
  sendAnswers,
  joinErrorMessage,
  type JoinMe,
  type PlayData,
  type PlayRound,
  type PlayView,
} from '../../utils/joinApi';
import {
  applyResults,
  backoffMs,
  loadQueue,
  lockRounds,
  mergeServer,
  pendingBatch,
  pendingCount,
  saveQueue,
  setDraft,
  type QueueState,
  type QueuedAnswer,
} from '../../utils/answerQueue';
import { choiceLetter, letterToIndex } from '../../utils/multipleChoice';

export interface LivePlayState {
  view: PlayView | null;
  lockedRounds: number[] | null;
}

interface Props {
  pin: string;
  me: JoinMe;
  live: LivePlayState | null;
  /** Changes when the server says the night or the team changed. */
  reloadKey: number;
}

const TYPE_DELAY_MS = 700;

export default function PhoneAnswers({ pin, me, live, reloadKey }: Props) {
  const [play, setPlay] = useState<PlayData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueState>(() => loadQueue(pin, me.teamId));
  const [locked, setLocked] = useState<number[]>([]);
  const [view, setView] = useState<PlayView | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [failures, setFailures] = useState(0);
  const [sendError, setSendError] = useState<string | null>(null);

  const queueRef = useRef(queue);
  queueRef.current = queue;
  const inFlight = useRef(false);
  const typeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failuresRef = useRef(0);

  // Keep a copy on the phone after every change.
  useEffect(() => {
    saveQueue(pin, me.teamId, queue);
  }, [pin, me.teamId, queue]);

  const loadPlay = useCallback(async () => {
    try {
      const data = await fetchPlay(pin);
      setPlay(data);
      setLoadError(null);
      if (data.lockedRounds) setLocked(data.lockedRounds);
      if (data.view !== undefined) setView(data.view ?? null);
      if (data.answers) setQueue((q) => mergeServer(q, data.answers!));
    } catch (err) {
      setLoadError(joinErrorMessage(err, 'Could not load the answer sheet.'));
    }
  }, [pin]);

  useEffect(() => {
    loadPlay();
  }, [loadPlay, reloadKey]);

  // Fallback poll in case the socket is down (it reconnects on its own).
  useEffect(() => {
    const t = setInterval(loadPlay, 45000);
    return () => clearInterval(t);
  }, [loadPlay]);

  // Live position and locks from the host.
  useEffect(() => {
    if (!live) return;
    if (live.view) setView(live.view);
    if (live.lockedRounds) setLocked(live.lockedRounds);
  }, [live]);

  const rounds = useMemo<PlayRound[]>(() => play?.rounds ?? [], [play]);

  // Pending answers in a closed round can never be sent.
  useEffect(() => {
    const closed = new Set<string>();
    for (const r of rounds) {
      if (locked.includes(r.roundNumber)) r.questions.forEach((q) => closed.add(q.id));
    }
    if (closed.size) setQueue((q) => lockRounds(q, closed));
  }, [locked, rounds]);

  // Follow the host to its round.
  const hostRound = view && 'roundNumber' in view && view.roundNumber ? view.roundNumber : null;
  useEffect(() => {
    if (hostRound) setSelected(hostRound);
  }, [hostRound]);
  useEffect(() => {
    if (selected == null && rounds.length) {
      const open = rounds.find((r) => !locked.includes(r.roundNumber));
      setSelected((open ?? rounds[0]!).roundNumber);
    }
  }, [rounds, locked, selected]);

  const flush = useCallback(async () => {
    if (inFlight.current) return;
    const batch = pendingBatch(queueRef.current);
    if (batch.length === 0) return;
    inFlight.current = true;
    setSending(true);
    let again = false;
    try {
      const res = await sendAnswers(pin, batch);
      setQueue((q) => applyResults(q, res.results));
      if (res.lockedRounds) setLocked(res.lockedRounds);
      failuresRef.current = 0;
      setFailures(0);
      setSendError(null);
      again = true;
    } catch (err) {
      failuresRef.current += 1;
      setFailures(failuresRef.current);
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      // 4xx other than 408/429 will not get better on retry.
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        setSendError(joinErrorMessage(err, 'The answers were not accepted.'));
      } else {
        if (retryTimer.current) clearTimeout(retryTimer.current);
        retryTimer.current = setTimeout(flush, backoffMs(failuresRef.current));
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
    // More edits arrived while this batch was in flight.
    if (again && pendingBatch(queueRef.current).length) setTimeout(flush, 0);
  }, [pin]);

  // Send again when the phone gets its connection back.
  useEffect(() => {
    const onOnline = () => flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flush]);

  // Send anything left from an earlier visit.
  useEffect(() => {
    if (play && pendingCount(queueRef.current) > 0) flush();
  }, [play, flush]);

  useEffect(
    () => () => {
      if (typeTimer.current) clearTimeout(typeTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
    },
    [],
  );

  const edit = (questionId: string, partA: string, partB: string | null) => {
    setQueue((q) => {
      const next = setDraft(q, questionId, partA, partB);
      queueRef.current = next;
      return next;
    });
    if (typeTimer.current) clearTimeout(typeTimer.current);
    typeTimer.current = setTimeout(flush, TYPE_DELAY_MS);
  };

  if (play && play.answerMode === 'PAPER') return null;

  if (!play) {
    return loadError ? (
      <Callout.Root color="amber" mt="4">
        <Callout.Text>{loadError}</Callout.Text>
        <Button size="1" variant="soft" onClick={loadPlay} mt="2">Try again</Button>
      </Callout.Root>
    ) : null;
  }

  const waiting = pendingCount(queue);
  const round = rounds.find((r) => r.roundNumber === selected) ?? null;
  const roundLocked = round ? locked.includes(round.roundNumber) : false;

  return (
    <Box mt="4">
      <div className="pa-status" role="status" aria-live="polite">
        {sendError ? (
          <Text color="red" size="2">{sendError}</Text>
        ) : waiting === 0 ? (
          <Text color="green" size="2">&#10003; All answers sent</Text>
        ) : (
          <Flex align="center" gap="2" justify="between" width="100%">
            <Text color="amber" size="2">
              {waiting} waiting to send{failures > 0 ? ' (no connection, retrying)' : sending ? '…' : ''}
            </Text>
            {failures > 0 && (
              <Button size="1" variant="soft" onClick={flush}>Retry now</Button>
            )}
          </Flex>
        )}
      </div>

      {play.answerMode === 'MIXED' && (
        <Text as="p" size="2" color="gray" mb="2">
          Answer here or on your paper sheet. Do not use both for the same round.
        </Text>
      )}

      {view && <HostPosition view={view} />}

      {rounds.length > 1 && (
        <div className="pa-rounds" role="tablist" aria-label="Rounds">
          {rounds.map((r) => {
            const closed = locked.includes(r.roundNumber);
            return (
              <button
                key={r.roundNumber}
                type="button"
                role="tab"
                aria-selected={selected === r.roundNumber}
                className={`pa-round-tab ${selected === r.roundNumber ? 'active' : ''} ${closed ? 'closed' : 'open'}`}
                onClick={() => setSelected(r.roundNumber)}
              >
                <span className="pa-round-num">R{r.roundNumber}</span>
                <span className="pa-round-state">{closed ? 'Closed' : 'Open'}</span>
              </button>
            );
          })}
        </div>
      )}

      {round && (
        <Card size="2" mt="3">
          <Flex justify="between" align="center" mb="3" gap="2">
            <Heading size="4">
              Round {round.roundNumber}
              {round.title ? `: ${round.title}` : ''}
            </Heading>
            <Badge color={roundLocked ? 'gray' : 'green'} size="2">{roundLocked ? 'Closed' : 'Open'}</Badge>
          </Flex>
          {roundLocked && (
            <Text as="p" size="2" color="gray" mb="2">
              The host closed this round. Answers here are no longer accepted.
            </Text>
          )}
          <Flex direction="column" gap="3">
            {round.questions.map((q) => {
              const current =
                view?.kind === 'QUESTION' && view.roundNumber === round.roundNumber && view.questionNumber === q.number;
              return q.choiceCount && q.choiceCount >= 2 ? (
                <ChoiceInput
                  key={q.id}
                  number={q.number}
                  count={q.choiceCount}
                  // The choice texts come only while the question is on the
                  // projector.
                  texts={current && view?.choices ? view.choices : null}
                  entry={queue[q.id]}
                  disabled={roundLocked}
                  current={current}
                  onPick={(letter) => edit(q.id, letter, null)}
                />
              ) : (
                <QuestionInput
                  key={q.id}
                  round={round}
                  number={q.number}
                  type={q.type}
                  entry={queue[q.id]}
                  disabled={roundLocked}
                  current={current}
                  onChange={(a, b) => edit(q.id, a, b)}
                  onBlur={flush}
                />
              );
            })}
          </Flex>
        </Card>
      )}
    </Box>
  );
}

function HostPosition({ view }: { view: PlayView }) {
  let line = '';
  if (view.kind === 'PREGAME') line = 'Starting soon';
  else if (view.kind === 'ROUND') line = `Round ${view.roundNumber}`;
  else if (view.kind === 'QUESTION') {
    line = `Round ${view.roundNumber} · ${view.questionType === 'SONG' ? 'Song' : 'Question'} ${view.questionNumber ?? ''}`;
    if (view.totalQuestions) line += ` of ${view.totalQuestions}`;
  } else if (view.kind === 'REVIEW') line = `Round ${view.roundNumber} answers`;
  else line = 'Break';
  return (
    <Card size="1" className="pa-now">
      <Text size="1" color="gray">On screen now</Text>
      <Text as="p" weight="bold" size="3">{line}</Text>
      {view.kind === 'QUESTION' && view.questionText && (
        <Text as="p" size="2" mt="1">{view.questionText}</Text>
      )}
    </Card>
  );
}

function statusBadge(entry: QueuedAnswer | undefined) {
  if (!entry || (!entry.partA && !entry.partB)) return null;
  if (entry.status === 'saved') return <Badge color="green">Sent</Badge>;
  if (entry.status === 'pending') return <Badge color="amber">Waiting</Badge>;
  if (entry.status === 'locked') return <Badge color="red">Not sent: round closed</Badge>;
  return <Badge color="red">Not sent</Badge>;
}

/**
 * A multiple choice question: tap a letter. The phone sends the letter
 * (for example "B") as the answer. The server marks it against the correct
 * choice, which never comes to the phone.
 */
function ChoiceInput({
  number,
  count,
  texts,
  entry,
  disabled,
  current,
  onPick,
}: {
  number: number;
  count: number;
  texts: string[] | null;
  entry: QueuedAnswer | undefined;
  disabled: boolean;
  current: boolean;
  onPick: (letter: string) => void;
}) {
  const picked = letterToIndex(entry?.partA);
  const label = `Question ${number}`;
  return (
    <div className={`pa-q ${current ? 'pa-q-current' : ''}`}>
      <Flex justify="between" align="center" mb="1" gap="2">
        <Text weight="bold" size="2">{label}</Text>
        {statusBadge(entry)}
      </Flex>
      <div
        className={`pa-choices ${texts ? 'with-text' : ''}`}
        role="radiogroup"
        aria-label={`${label} choices`}
      >
        {Array.from({ length: count }, (_, i) => {
          const letter = choiceLetter(i);
          const on = picked === i;
          return (
            <Button
              key={letter}
              type="button"
              size="3"
              role="radio"
              aria-checked={on}
              variant={on ? 'solid' : 'soft'}
              color={on ? undefined : 'gray'}
              disabled={disabled}
              className="pa-choice"
              onClick={() => onPick(letter)}
            >
              <span className="pa-choice-letter">{letter}</span>
              {texts && texts[i] ? <span className="pa-choice-text">{texts[i]}</span> : null}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function QuestionInput({
  round,
  number,
  type,
  entry,
  disabled,
  current,
  onChange,
  onBlur,
}: {
  round: PlayRound;
  number: number;
  type: 'TEXT' | 'SONG' | 'IMAGE';
  entry: QueuedAnswer | undefined;
  disabled: boolean;
  current: boolean;
  onChange: (partA: string, partB: string | null) => void;
  onBlur: () => void;
}) {
  const a = entry?.partA ?? '';
  const b = entry?.partB ?? '';
  // A song can be any question in any round: it always takes a title and
  // an artist, which the host marks as two parts.
  const isSong = type === 'SONG' || round.roundType === 'AUDIO';
  const twoPart = isSong || (round.roundType === 'STANDARD' && round.twoAnswerMode);
  const label = isSong
    ? `Song ${number}`
    : round.roundType === 'VISUAL' ? `Item ${number}` : `Question ${number}`;
  const [labelA, labelB] = isSong ? ['Song title', 'Artist'] : ['Part A', 'Part B'];

  return (
    <div className={`pa-q ${current ? 'pa-q-current' : ''}`}>
      <Flex justify="between" align="center" mb="1" gap="2">
        <Text weight="bold" size="2">{label}</Text>
        {statusBadge(entry)}
      </Flex>
      {twoPart ? (
        <Flex direction="column" gap="2">
          <TextField.Root
            size="3"
            value={a}
            placeholder={labelA}
            aria-label={`${label} ${labelA}`}
            maxLength={200}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => onChange(e.target.value, b)}
            onBlur={onBlur}
          />
          <TextField.Root
            size="3"
            value={b}
            placeholder={labelB}
            aria-label={`${label} ${labelB}`}
            maxLength={200}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => onChange(a, e.target.value)}
            onBlur={onBlur}
          />
        </Flex>
      ) : (
        <TextField.Root
          size="3"
          value={a}
          placeholder="Your answer"
          aria-label={`${label} answer`}
          maxLength={200}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value, null)}
          onBlur={onBlur}
        />
      )}
    </div>
  );
}
